import { HStack } from '@chakra-ui/react';
import { RequiredMark } from '@main/complykit/components/field';
import { Status } from '@main/complykit/components/status';
import { Text } from '@main/complykit/components/text';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuestionnnaireFormContext } from './context';
import {
  QuestionnaireFormFieldConfig,
  QuestionnnaireFieldWrapperProps,
  QuestionnnaireFormFieldAnswer,
} from './form-types';
import { QUESTION_STATUSES } from './utils';

export interface FieldCollapsedHeaderProps extends QuestionnnaireFieldWrapperProps {
  config: QuestionnaireFormFieldConfig;
  answer: QuestionnnaireFormFieldAnswer;
  /** Collapsed hides the field's error message, so the invalid state needs a
   * visible label here: a red border alone conveys nothing without colour. */
  isInvalid?: boolean;
  /** Resolved by the engine: a read-only answer is never marked required. */
  isRequired?: boolean;
}

export function FieldCollapsedHeader(props: FieldCollapsedHeaderProps) {
  const { t } = useTranslation();
  const { questionFields } = useQuestionnnaireFormContext();

  const { config } = props;
  const totalQuestions = questionFields.length;
  const questionIdx = useMemo(
    () => questionFields.findIndex((field) => field.name === config.name) + 1,
    [config.name, questionFields],
  );

  return (
    <HStack fontSize="sm">
      <Text size="xs" weight="medium" isNoWrap>
        {t('questionnaires.form.questionNo', { number: questionIdx, total: totalQuestions })}
      </Text>
      <Status tone={QUESTION_STATUSES[props.answer.status].tone}>
        {t(QUESTION_STATUSES[props.answer.status].value)}
      </Status>
      <Text size="sm" lines={1}>
        {config.label}
      </Text>
      {/* Read aloud: the collapsed answer is hidden, so no control announces aria-required. */}
      {props.isRequired && <RequiredMark announces />}
      {props.isInvalid && (
        <Text as="span" size="xs" weight="medium" color="error" isNoWrap>
          {t('questionnaires.form.invalidAnswer')}
        </Text>
      )}
    </HStack>
  );
}
